import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Col, Flex, Row, Switch, Spin, Tooltip } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { colors } from "themes/variables"
import {
  filterPermissionsByColumn,
  transformPermissionHashToUI,
  getInitialExpandedFromPermissions,
  getInitialSelectedTabsFromPermissions,
  buildAssignPermissionPayload
} from "../utils/rolesAccessHelpers"
import styles from "../index.module.css"

const SubPermission = memo(
  ({ subPerm, selectedTabs, onTabChange, onToggle, isAnyUpdateInProgress, isVisible, styles, shouldShowScope }) => {
    if (!isVisible) {
      return null
    }

    const shouldDisplayScope = shouldShowScope !== undefined ? shouldShowScope : true

    return (
      <Flex gap={12} align="center" justify="space-between" className={styles.accessItem} wrap>
        <div className={`${styles.subPermissionItem} ${isAnyUpdateInProgress ? styles.subPermissionItemDisabled : ""}`}>
          <Switch
            checked={subPerm.enabled}
            onChange={(checked) => !isAnyUpdateInProgress && onToggle(subPerm, checked)}
            size="small"
            className={isAnyUpdateInProgress ? styles.subPermissionSwitch : ""}
          />
          <Text className={styles.accessLabel}>{subPerm.label}</Text>
        </div>
        {subPerm?.options && subPerm?.enabled && shouldDisplayScope && (
          <div className={`${styles.tabGroup} ${isAnyUpdateInProgress ? styles.tabGroupDisabled : ""}`}>
            {subPerm?.options?.map((option) => {
              const isActive = (selectedTabs[subPerm.id] || subPerm?.selectedScope || subPerm?.options?.[0]) === option
              return (
                <button
                  key={option}
                  className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""} ${
                    isAnyUpdateInProgress ? styles.tabButtonDisabled : ""
                  }`}
                  onClick={() => !isAnyUpdateInProgress && onTabChange(subPerm.id, option)}
                >
                  <Text color={isActive ? "var(--color-primary)" : colors?.textSecondaryDark} className={styles.tabButtonText}>
                    {option}
                  </Text>
                </button>
              )
            })}
          </div>
        )}
      </Flex>
    )
  }
)

SubPermission.displayName = "SubPermission"

SubPermission.propTypes = {
  subPerm: PropTypes.object.isRequired,
  selectedTabs: PropTypes.object.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  isAnyUpdateInProgress: PropTypes.bool,
  isVisible: PropTypes.bool,
  shouldShowScope: PropTypes.bool,
  styles: PropTypes.object.isRequired
}

const PermissionGroup = memo(
  ({
    permission,
    isExpanded,
    onToggle,
    selectedTabs,
    onTabChange,
    onSubPermissionToggle,
    onFullAccessToggle,
    updatingPermissions,
    isAnyUpdateInProgress,
    styles,
    t
  }) => {
    const isModuleUpdating = updatingPermissions[permission.id]

    const viewPermission = permission?.subPermissions?.find((sp) => sp.key === "view")
    const isViewEnabled = viewPermission?.enabled === true

    const shouldShowPermission = (subPerm) => {
      if (subPerm.key === "view") return true

      if (viewPermission) {
        const dependentActions = ["create", "edit", "delete", "disable", "deactivate", "import"]
        if (dependentActions.includes(subPerm.key)) {
          return isViewEnabled
        }
      }

      return true
    }

    const shouldShowScopeForPermission = (subPerm) => {
      if (permission.id === "bookings" && subPerm.key === "edit") {
        if (!viewPermission || !viewPermission.availableScopes || viewPermission.availableScopes.length <= 1) {
          return true
        }
        const viewScopeLabel = selectedTabs[viewPermission.id] || viewPermission.selectedScope || viewPermission.options?.[0]
        const currentViewScope = viewPermission.availableScopes.find((s) => s.label === viewScopeLabel)
        const allBookingsScope = viewPermission.availableScopes[0]
        if (currentViewScope && allBookingsScope && currentViewScope.id === allBookingsScope.id) {
          return true
        }
        return false
      }

      if (permission.id === "experiences" && subPerm.key === "edit") {
        if (!viewPermission || !viewPermission.availableScopes || viewPermission.availableScopes.length <= 1) {
          return true
        }
        const viewScopeLabel = selectedTabs[viewPermission.id] || viewPermission.selectedScope || viewPermission.options?.[0]
        const currentViewScope = viewPermission.availableScopes.find((s) => s.label === viewScopeLabel)
        const allRecordsScope = viewPermission.availableScopes[0]
        if (currentViewScope && allRecordsScope && currentViewScope.id === allRecordsScope.id) {
          return true
        }
        return false
      }

      return true
    }

    return (
      <div className={`${styles.accessGroup} ${styles.tabGroupRelative}`}>
        <div className={`${styles.accessHeader} ${isAnyUpdateInProgress && !isModuleUpdating ? styles.accessHeaderDisabled : ""}`}>
          <div className={styles.accessIcon}>
            <Icon type={permission.iconType || "dsicon"} name={permission.iconName} width={20} height={20} color={colors.textSecondary} />
          </div>
          <Flex align="center" gap={6}>
            <Text className={styles.accessTitle}>{permission.title}</Text>
            {permission.helpText && (
              <Tooltip title={permission.helpText} placement="top">
                <Icon type="feather" name="Info" width={16} height={16} color={colors.textSecondary} style={{ cursor: "help" }} />
              </Tooltip>
            )}
          </Flex>
          <Switch
            checked={permission.hasOnlyFullAccess ? permission.isEnabled : isExpanded}
            onChange={() => {
              if (isAnyUpdateInProgress && !isModuleUpdating) return
              permission.hasOnlyFullAccess ? onFullAccessToggle(permission) : onToggle(permission)
            }}
            className={`${styles.accessSwitch} ${isAnyUpdateInProgress && !isModuleUpdating ? styles.accessSwitchDisabled : ""}`}
            loading={permission.hasOnlyFullAccess && isModuleUpdating}
          />
        </div>
        {!permission?.hasOnlyFullAccess && isExpanded && permission?.subPermissions?.length > 0 && (
          <div className={styles.accessDetails}>
            {permission?.subPermissions?.map((subPerm) => (
              <SubPermission
                key={subPerm.id}
                subPerm={subPerm}
                selectedTabs={selectedTabs}
                onTabChange={onTabChange}
                onToggle={(subPerm, checked) => onSubPermissionToggle(permission, subPerm, checked)}
                isAnyUpdateInProgress={isAnyUpdateInProgress}
                isVisible={shouldShowPermission(subPerm)}
                shouldShowScope={shouldShowScopeForPermission(subPerm)}
                styles={styles}
              />
            ))}
          </div>
        )}
        {isModuleUpdating && (
          <div className={styles.tabLoadingOverlay}>
            <Spin size="small" />
          </div>
        )}
      </div>
    )
  }
)

PermissionGroup.displayName = "PermissionGroup"

PermissionGroup.propTypes = {
  permission: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  selectedTabs: PropTypes.object.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onSubPermissionToggle: PropTypes.func.isRequired,
  onFullAccessToggle: PropTypes.func.isRequired,
  updatingPermissions: PropTypes.object,
  isAnyUpdateInProgress: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  t: PropTypes.func
}

const PermissionsPanel = ({ selectedRole, t, onPermissionChange, loading = false }) => {
  const [expandedPermissions, setExpandedPermissions] = useState({})
  const [selectedTabs, setSelectedTabs] = useState({})
  const [updatingPermissions, setUpdatingPermissions] = useState({})
  const [isAnyUpdateInProgress, setIsAnyUpdateInProgress] = useState(false)
  const updatingPermissionsRef = useRef({})

  const dynamicPermissionsData = useMemo(() => {
    const permissionData = selectedRole?.permissions
    if (permissionData) {
      return transformPermissionHashToUI(permissionData, t)
    }
    return []
  }, [selectedRole, t])

  useEffect(() => {
    const permissionData = selectedRole?.permissions
    if (permissionData) {
      const initialExpanded = getInitialExpandedFromPermissions(permissionData)
      const initialTabs = getInitialSelectedTabsFromPermissions(permissionData)
      setExpandedPermissions(initialExpanded)
      setSelectedTabs(initialTabs)
    } else {
      setExpandedPermissions({})
      setSelectedTabs({})
    }
  }, [selectedRole])

  const handleMainToggle = useCallback(
    async (permission) => {
      if (!onPermissionChange || !selectedRole) {
        setExpandedPermissions((prev) => ({
          ...prev,
          [permission.id]: !prev[permission.id]
        }))
        return
      }

      const isCurrentlyExpanded = expandedPermissions[permission.id]
      const hasAnyEnabled = permission.subPermissions?.some((sp) => sp.enabled)
      const isTogglingOff = isCurrentlyExpanded && hasAnyEnabled

      if (updatingPermissionsRef.current[permission.id]) {
        return
      }

      if (!isTogglingOff) {
        const viewPermission = permission.subPermissions?.find((sp) => sp.key === "view")

        if (viewPermission && !viewPermission.enabled) {
          updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
          setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
          setIsAnyUpdateInProgress(true)

          try {
            let scopeValue = null

            if (viewPermission.options && viewPermission.options.length > 0) {
              const scopeLabel = selectedTabs[viewPermission.id] || viewPermission.selectedScope || viewPermission.options[0]
              const scopeItem = viewPermission.availableScopes?.find((s) => s.label === scopeLabel)
              scopeValue = scopeItem?.id || scopeLabel
            }

            const moduleId = permission.module_id || permission.access_module_id
            const payload = buildAssignPermissionPayload(moduleId, viewPermission, true, scopeValue)

            await onPermissionChange(selectedRole.id, payload)

            setExpandedPermissions((prev) => ({
              ...prev,
              [permission.id]: true
            }))
          } finally {
            updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
            setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
            setIsAnyUpdateInProgress(false)
          }
        } else {
          setExpandedPermissions((prev) => ({
            ...prev,
            [permission.id]: !prev[permission.id]
          }))
        }
        return
      }

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        const permissionsAttributes = []

        permission.subPermissions?.forEach((subPerm) => {
          let permissionScopeId = null
          if (subPerm.scope_id !== null && subPerm.scope_id !== undefined) {
            permissionScopeId = subPerm.scope_id
          } else if (subPerm.availableScopes?.length > 0) {
            permissionScopeId = subPerm.availableScopes[0]?.id
          }

          const permissionAttribute = {
            id: subPerm.permission_id || null,
            access_screen_id: subPerm.access_screen_id,
            permission_action_id: subPerm.action_id,
            permission_scope_id: permissionScopeId,
            is_active: false
          }

          permissionsAttributes.push(permissionAttribute)
        })

        const payload = {
          role: {
            permissions_attributes: permissionsAttributes
          }
        }

        await onPermissionChange(selectedRole.id, payload)

        setExpandedPermissions((prev) => ({
          ...prev,
          [permission.id]: false
        }))
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole, expandedPermissions, selectedTabs]
  )

  const handleSubPermissionToggle = useCallback(
    async (permission, subPerm, isEnabled) => {
      if (!onPermissionChange || !selectedRole) return

      if (updatingPermissionsRef.current[permission.id]) {
        return
      }

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        let scopeValue = null

        if (isEnabled && subPerm.options && subPerm.options.length > 0) {
          if ((permission.id === "bookings" || permission.id === "experiences") && subPerm.key === "edit") {
            const viewPermission = permission.subPermissions?.find((sp) => sp.key === "view")
            if (viewPermission) {
              const viewScope = selectedTabs[viewPermission.id] || viewPermission.selectedScope || viewPermission.options?.[0]
              const scopeItem = subPerm.availableScopes?.find((s) => s.label === viewScope)
              scopeValue = scopeItem?.id || viewScope
            } else {
              const scopeLabel = selectedTabs[subPerm.id] || subPerm.selectedScope || subPerm.options[0]
              const scopeItem = subPerm.availableScopes?.find((s) => s.label === scopeLabel)
              scopeValue = scopeItem?.id || scopeLabel
            }
          } else {
            const scopeLabel = selectedTabs[subPerm.id] || subPerm.selectedScope || subPerm.options[0]
            const scopeItem = subPerm.availableScopes?.find((s) => s.label === scopeLabel)
            scopeValue = scopeItem?.id || scopeLabel
          }
        }

        const moduleId = permission.module_id || permission.access_module_id
        const payload = buildAssignPermissionPayload(moduleId, subPerm, isEnabled, scopeValue)

        await onPermissionChange(selectedRole.id, payload)
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole, selectedTabs]
  )

  const handleTabChange = useCallback(
    async (permissionId, option) => {
      if (!onPermissionChange || !selectedRole) return

      const [moduleId] = permissionId.split("-")
      const permission = dynamicPermissionsData.find((p) => p.id === moduleId)
      const subPerm = permission?.subPermissions.find((sp) => sp.id === permissionId)

      if (!permission || !subPerm) return

      if (updatingPermissionsRef.current[permission.id]) {
        return
      }

      const scopeItem = subPerm.availableScopes?.find((s) => s.label === option)
      const scopeApiValue = scopeItem?.id

      if (scopeApiValue === null || scopeApiValue === undefined) return

      const previousScope = selectedTabs[permissionId] || subPerm.selectedScope || subPerm.options?.[0]

      setSelectedTabs((prev) => ({
        ...prev,
        [permissionId]: option
      }))

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        const moduleId = permission.module_id || permission.access_module_id

        if ((permission.id === "bookings" || permission.id === "experiences") && subPerm.key === "view") {
          const editPermission = permission.subPermissions?.find((sp) => sp.key === "edit")

          if (editPermission && editPermission.enabled) {
            const editScopeItem = editPermission.availableScopes?.find((s) => s.label === option)
            const editScopeApiValue = editScopeItem?.id

            if (editScopeApiValue !== null && editScopeApiValue !== undefined) {
              setSelectedTabs((prev) => ({
                ...prev,
                [editPermission.id]: option
              }))

              const permissionsAttributes = []

              const viewPermAttr = {
                id: subPerm.permission_id || null,
                access_screen_id: subPerm.access_screen_id,
                permission_action_id: subPerm.action_id,
                permission_scope_id: scopeApiValue,
                is_active: true
              }
              permissionsAttributes.push(viewPermAttr)

              const editPermAttr = {
                id: editPermission.permission_id || null,
                access_screen_id: editPermission.access_screen_id,
                permission_action_id: editPermission.action_id,
                permission_scope_id: editScopeApiValue,
                is_active: true
              }
              permissionsAttributes.push(editPermAttr)

              const combinedPayload = {
                role: {
                  permissions_attributes: permissionsAttributes
                }
              }

              await onPermissionChange(selectedRole.id, combinedPayload)
              return
            }
          }
        }

        const payload = buildAssignPermissionPayload(moduleId, subPerm, true, scopeApiValue)
        await onPermissionChange(selectedRole.id, payload)
      } catch (error) {
        setSelectedTabs((prev) => ({
          ...prev,
          [permissionId]: previousScope
        }))
        throw error
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole, dynamicPermissionsData, selectedTabs]
  )

  const handleFullAccessToggle = useCallback(
    async (permission) => {
      if (!onPermissionChange || !selectedRole) return

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        const isTogglingOff = permission.isEnabled
        const permissionsAttributes = []

        if (isTogglingOff) {
          permission.subPermissions?.forEach((subPerm) => {
            let permissionScopeId = null
            if (subPerm?.availableScopes?.length > 0) {
              permissionScopeId = subPerm.availableScopes[0]?.id
            }

            permissionsAttributes.push({
              id: subPerm?.permission_id || null,
              access_screen_id: subPerm?.access_screen_id || null,
              permission_action_id: subPerm?.action_id,
              permission_scope_id: permissionScopeId,
              is_active: false
            })
          })
        } else {
          const fullAccessPerm = permission.subPermissions?.[0]

          let permissionScopeId = null
          if (fullAccessPerm?.availableScopes?.length > 0) {
            permissionScopeId = fullAccessPerm.availableScopes[0]?.id
          }

          permissionsAttributes?.push({
            id: fullAccessPerm?.permission_id || null,
            access_screen_id: fullAccessPerm?.access_screen_id || null,
            permission_action_id: fullAccessPerm?.action_id,
            permission_scope_id: permissionScopeId,
            is_active: true
          })
        }

        const payload = {
          role: {
            permissions_attributes: permissionsAttributes
          }
        }

        await onPermissionChange(selectedRole.id, payload)
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole]
  )

  const leftColumnPermissions = useMemo(() => filterPermissionsByColumn(dynamicPermissionsData, "left"), [dynamicPermissionsData])
  const rightColumnPermissions = useMemo(() => filterPermissionsByColumn(dynamicPermissionsData, "right"), [dynamicPermissionsData])

  if (loading) {
    return (
      <div className={styles.accessSection}>
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Spin size="large" />
        </Flex>
      </div>
    )
  }

  if (!selectedRole) {
    return (
      <div className={styles.accessSection}>
        <div className={styles.emptyState}>
          <Text className={styles.emptyStateText}>{t("staff.roles_access.no_role_selected")}</Text>
        </div>
      </div>
    )
  }

  const permissionData = selectedRole.permissions
  const hasPermissions = permissionData && Array.isArray(permissionData) && permissionData.length > 0

  if (!hasPermissions) {
    return (
      <div className={styles.accessSection}>
        <Text className={styles.sectionTitle}>
          {t("staff.roles_access.access_for")} {selectedRole?.name || ""}
        </Text>
        <div className={styles.emptyState}>
          <Text className={styles.emptyStateText}>{t("staff.roles_access.no_permissions_for_role")}</Text>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.accessSection}>
      <Text className={styles.sectionTitle}>
        {t("staff.roles_access.access_for")} {selectedRole?.name || ""}
      </Text>
      <Row gutter={[12, 0]}>
        <Col xs={24} lg={24} xl={12}>
          {leftColumnPermissions.map((permission) => (
            <PermissionGroup
              key={permission.id}
              permission={permission}
              isExpanded={expandedPermissions[permission.id]}
              onToggle={handleMainToggle}
              selectedTabs={selectedTabs}
              onTabChange={handleTabChange}
              onSubPermissionToggle={handleSubPermissionToggle}
              onFullAccessToggle={handleFullAccessToggle}
              updatingPermissions={updatingPermissions}
              isAnyUpdateInProgress={isAnyUpdateInProgress}
              styles={styles}
              t={t}
            />
          ))}
        </Col>
        <Col xs={24} lg={24} xl={12}>
          {rightColumnPermissions.map((permission) => (
            <PermissionGroup
              key={permission.id}
              permission={permission}
              isExpanded={expandedPermissions[permission.id]}
              onToggle={handleMainToggle}
              selectedTabs={selectedTabs}
              onTabChange={handleTabChange}
              onSubPermissionToggle={handleSubPermissionToggle}
              onFullAccessToggle={handleFullAccessToggle}
              updatingPermissions={updatingPermissions}
              isAnyUpdateInProgress={isAnyUpdateInProgress}
              styles={styles}
              t={t}
            />
          ))}
        </Col>
      </Row>
    </div>
  )
}

PermissionsPanel.propTypes = {
  selectedRole: PropTypes.object,
  t: PropTypes.func.isRequired,
  onPermissionChange: PropTypes.func,
  loading: PropTypes.bool
}

export default memo(PermissionsPanel)
